import React from 'react';
import { VisualizationEvent, PositionData } from '../types';
import { transformPoints, PITCH_WIDTH, PITCH_HEIGHT, RotationAngle } from '../utils';

interface ScatterPlotProps {
  events: VisualizationEvent[];
  rotation?: RotationAngle;
  scale?: number;
  colorBy?: 'team' | 'stat_type';
  teamColors?: Record<string, string>;
  statTypeColors?: Record<string, string>;
  scatterShapes?: Record<string, 'circle' | 'square' | 'triangle' | 'star'>;
  pointSize?: number;
}

/**
 * Source scatter plot visualization component
 * Renders the first (source) point of events on the pitch
 */
const ScatterPlot: React.FC<ScatterPlotProps> = ({
  events,
  rotation = 0,
  scale = 1.0,
  colorBy = 'team',
  teamColors = {},
  statTypeColors = {},
  scatterShapes = {},
  pointSize = 6,
}) => {
  // Default team colors
  const defaultTeamColors: Record<string, string> = {
    default: '#3B82F6', // Blue
  };

  const getPointColor = (event: VisualizationEvent): string => {
    switch (colorBy) {
      case 'team':
        return teamColors[event.team_id] || defaultTeamColors.default;
      case 'stat_type':
        // Use team-specific color if set (key format: `${statTypeId}-${teamId}`)
        // Fall back to stat-type-only color, then generate from hash
        if (event.team_id) {
          const teamSpecificKey = `${event.stat_type_id}-${event.team_id}`;
          if (statTypeColors[teamSpecificKey]) {
            return statTypeColors[teamSpecificKey];
          }
        }
        if (statTypeColors[event.stat_type_id]) {
          return statTypeColors[event.stat_type_id];
        }
        // Generate hash from statTypeId ONLY (not including teamId) to get same color for same stat type across teams
        // Users can still customize per-team by setting team-specific colors via statTypeColors[`${statTypeId}-${teamId}`]
        const hash = event.stat_type_id.split('').reduce((acc, char) => {
          return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);
        const hue = Math.abs(hash) % 360;
        // Convert HSL to hex
        const h = hue / 360;
        const s = 0.7;
        const l = 0.5;
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((h * 6) % 2 - 1));
        const m = l - c / 2;
        let r = 0, g = 0, b = 0;
        if (h < 1/6) { r = c; g = x; b = 0; }
        else if (h < 2/6) { r = x; g = c; b = 0; }
        else if (h < 3/6) { r = 0; g = c; b = x; }
        else if (h < 4/6) { r = 0; g = x; b = c; }
        else if (h < 5/6) { r = x; g = 0; b = c; }
        else { r = c; g = 0; b = x; }
        const toHex = (n: number) => {
          const hex = Math.round((n + m) * 255).toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        };
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      default:
        return defaultTeamColors.default;
    }
  };

  // Extract points from events (use first point of position_data or a default)
  const points = events
    .map((event) => {
      if (event.position_data && event.position_data.length > 0) {
        // Use the first point for scatter plot
        return {
          point: event.position_data[0],
          color: getPointColor(event),
          event,
        };
      }
      return null;
    })
    .filter((item): item is { point: PositionData; color: string; event: VisualizationEvent } => item !== null);

  // Get shape for an event based on colorBy
  const getShape = (event: VisualizationEvent): 'circle' | 'square' | 'triangle' | 'star' => {
    if (colorBy === 'team' && scatterShapes[event.team_id]) {
      return scatterShapes[event.team_id];
    }
    if (colorBy === 'stat_type' && scatterShapes[event.stat_type_id]) {
      return scatterShapes[event.stat_type_id];
    }
    return 'circle'; // Default
  };

  // Render a point based on shape
  const renderPoint = (x: number, y: number, color: string, shape: 'circle' | 'square' | 'triangle' | 'star', key: string) => {
    const size = pointSize;
    const strokeWidth = 2;
    
    switch (shape) {
      case 'square':
        return (
          <rect
            key={key}
            x={x - size}
            y={y - size}
            width={size * 2}
            height={size * 2}
            fill={color}
            stroke="#ffffff"
            strokeWidth={strokeWidth}
            opacity={0.8}
            style={{ cursor: 'pointer' }}
          />
        );
      case 'triangle':
        return (
          <polygon
            key={key}
            points={`${x},${y - size} ${x - size * 0.866},${y + size * 0.5} ${x + size * 0.866},${y + size * 0.5}`}
            fill={color}
            stroke="#ffffff"
            strokeWidth={strokeWidth}
            opacity={0.8}
            style={{ cursor: 'pointer' }}
          />
        );
      case 'star':
        const starPoints: string[] = [];
        const outerRadius = size;
        const innerRadius = size * 0.4;
        for (let i = 0; i < 10; i++) {
          const angle = (i * Math.PI) / 5 - Math.PI / 2;
          const radius = i % 2 === 0 ? outerRadius : innerRadius;
          starPoints.push(`${x + radius * Math.cos(angle)},${y + radius * Math.sin(angle)}`);
        }
        return (
          <polygon
            key={key}
            points={starPoints.join(' ')}
            fill={color}
            stroke="#ffffff"
            strokeWidth={strokeWidth}
            opacity={0.8}
            style={{ cursor: 'pointer' }}
          />
        );
      case 'circle':
      default:
        return (
          <circle
            key={key}
            cx={x}
            cy={y}
            r={size}
            fill={color}
            stroke="#ffffff"
            strokeWidth={strokeWidth}
            opacity={0.8}
            style={{ cursor: 'pointer' }}
          />
        );
    }
  };

  // Transform points based on rotation and scale
  const transformedPoints = points.map(({ point, color, event }) => ({
    point: transformPoints([point], rotation, scale, { width: PITCH_WIDTH, height: PITCH_HEIGHT })[0],
    color,
    event,
    shape: getShape(event),
  }));

  return (
    <g id="source-scatter-plot">
      {transformedPoints.map(({ point, color, event, shape }, index) =>
        renderPoint(point.x, point.y, color, shape, `source-scatter-${event.id}-${index}`)
      )}
    </g>
  );
};

export default ScatterPlot;

